import { Calendar, CalendarClock, Check, ChevronDown, Edit, FileCheck, Plus, Save, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AddBracketModal from '../../components/AddBracketModal'
import BackLink from '../../components/BackLink'
import Layout from '../../components/Layout'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import { Input } from '../../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { useNotification } from '../../contexts/NotificationContext'
import { buildEasternIsoString, utcToEastern } from '../../lib/dateUtils'
import { updateBracketStatus } from '../../services/bracketService'
import {
  createEntry as createEntryService,
  deleteBracket as deleteBracketService,
  deleteTournament as deleteTournamentService,
  fetchBracketOfficialEntries,
  fetchTournament,
  updateTournament as updateTournamentService,
} from '../../services/tournamentService'

function statusBadgeVariant(status) {
  switch (status) {
    case 'open': return 'teal'
    case 'locked': return 'blue'
    case 'draft': return 'orange'
    case 'complete': return 'teal'
    default: return 'muted'
  }
}

function statusLabel(status) {
  switch (status) {
    case 'open': return 'Open for picks'
    case 'locked': return 'Locked'
    case 'draft': return 'Draft'
    case 'complete': return 'Complete'
    default: return status || 'Unknown'
  }
}

// Statuses where the admin dropdown is active. Complete is terminal — the
// bracket has finished and lock state is moot. Draft is editable so an admin
// can transition out of it (the dropdown just doesn't offer Draft as a target).
const EDITABLE_STATUSES = ['draft', 'open', 'locked']
const STATUS_DROPDOWN_OPTIONS = [
  { value: 'open', label: 'Open for picks', dot: 'bg-[var(--teal-9)]' },
  { value: 'locked', label: 'Locked', dot: 'bg-[var(--blue-9)]' },
]

export default function TournamentEdit() {
  const navigate = useNavigate()
  const { id } = useParams()
  const notify = useNotification()
  const [loading, setLoading] = useState(true)
  const [sportId, setSportId] = useState(null)
  const [brackets, setBrackets] = useState([])
  const [showAddBracketModal, setShowAddBracketModal] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    startDate: '',
    endDate: '',
    submissionDeadline: '',
    submissionDeadlineTime: '',
    status: 'draft',
    isTest: false,
  })

  const loadTournament = async () => {
    try {
      setLoading(true)
      const [data, officialEntries] = await Promise.all([
        fetchTournament({ tournamentId: id }),
        fetchBracketOfficialEntries({ tournamentId: id }).catch(() => []),
      ])

      if (data) {
        const easternDeadline = utcToEastern(data.submissionDeadline)

        setFormData({
          title: data.name || '',
          startDate: data.startDate?.split('T')[0] || '',
          endDate: data.endDate?.split('T')[0] || '',
          submissionDeadline: easternDeadline.date || '',
          submissionDeadlineTime: easternDeadline.time || '23:59',
          status: data.status || 'draft',
          isTest: !!data.isTest,
        })
        setSportId(data.sport?.id || null)
        const officialByBracket = new Map(
          (officialEntries || []).map(row => [row.id, row.official_entry_id || null])
        )
        setBrackets((data.brackets || []).map(b => ({
          id: b.id,
          divisionId: b.divisionId,
          // Pool Play brackets are division-less; show a stable label instead.
          name: b.divisionName || (b.format === 'pool_then_sunday' ? 'Pool Play' : 'Bracket'),
          format: b.format || 'single_elim',
          status: b.status || 'draft',
          entryCount: b.entryCount || 0,
          officialEntryId: officialByBracket.get(b.id) || null,
        })))
      }
    } catch (error) {
      console.error('Error loading tournament:', error)
      notify.error('Failed to load tournament')
    } finally {
      setLoading(false)
    }
  }

  const handleEnterOfficialPicks = async (bracket) => {
    try {
      if (!bracket.officialEntryId) {
        const entry = await createEntryService({ bracketId: bracket.id, isOfficial: true })
        const entryId = entry?.entry_id || entry?.id
        if (!entryId) throw new Error('create_entry returned no id')
        setBrackets(prev => prev.map(b => (b.id === bracket.id ? { ...b, officialEntryId: entryId } : b)))
      }
      // Use the literal "official" marker so useBracketStore flips into
      // admin-edit-official mode. Passing the UUID would read-only-view it.
      // mode=editOfficial marks this as an EXPLICIT admin intent to edit —
      // distinct from an admin who happened to land on ?entryId=official via
      // the locked-bracket fallback while browsing the user flow.
      navigate(`/bracket/${bracket.id}/picks?entryId=official&mode=editOfficial`)
    } catch (err) {
      console.error('Error opening official entry:', err)
      notify.error('Failed to open official picks. Please try again.')
    }
  }

  useEffect(() => {
    loadTournament()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleInputChange = (field, value) => {
    // DUB-50: when start date changes, recompute the submission deadline to
    // the day before. Matches TournamentCreate behavior; admin can still edit
    // the deadline manually afterward.
    if (field === 'startDate' && value) {
      const startDate = new Date(value)
      const dayBefore = new Date(startDate)
      dayBefore.setDate(dayBefore.getDate() - 1)
      const submissionDeadline = dayBefore.toISOString().split('T')[0]
      setFormData(prev => ({
        ...prev,
        [field]: value,
        submissionDeadline,
      }))
      return
    }
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const buildTournamentPayload = (statusValue) => ({
    tournamentId: id,
    name: formData.title,
    startDate: formData.startDate ? `${formData.startDate}T00:00:00` : null,
    endDate: formData.endDate ? `${formData.endDate}T23:59:59` : null,
    submissionDeadline: formData.submissionDeadline && formData.submissionDeadlineTime
      ? buildEasternIsoString(formData.submissionDeadline, formData.submissionDeadlineTime)
      : null,
    status: statusValue,
    isTest: formData.isTest,
  })

  const handleSubmit = async () => {
    try {
      await updateTournamentService(buildTournamentPayload(formData.status))

      notify.success('Tournament updated successfully!')

      // DUB-79 / DUB-80 / DUB-81: when a tournament moves draft → published,
      // the update_tournament RPC cascades any draft brackets to 'open'
      // (validated server-side). The cascade actually runs, but this page's
      // React state never refetched — so admins kept seeing the pre-save
      // bracket status and reported "brackets stayed Draft" while the user
      // view (which fetches fresh) correctly showed picks editable. Refetch
      // after save so the UI reflects the cascaded statuses.
      await loadTournament()

      // DUB-46: stay on edit page after save so admins can keep iterating.
    } catch (error) {
      console.error('Error updating tournament:', error)
      notify.error(error.message || 'Failed to update tournament. Please try again.')
      // The save is atomic server-side: a rejected publish (e.g. no brackets)
      // rolls back, so the persisted status is unchanged. Resync the form from
      // the server so the dropdown reflects reality instead of the unsaved
      // selection the admin just made (DUB-148: status stayed "Published" in
      // the UI even though the publish was blocked).
      await loadTournament()
    }
  }

  // DUB-148: the Status change is an immediate action (like the per-bracket
  // status dropdown below), not a deferred edit. Selecting "Published" persists
  // right away; if the server rejects it (no brackets, or an unfilled pool that
  // can't open) the dropdown snaps straight back to its prior value with the
  // reason — instead of misleadingly showing "Published" until Save is clicked.
  const handleStatusChange = async (newStatus) => {
    if (newStatus === formData.status) return
    const previous = formData.status
    setFormData(prev => ({ ...prev, status: newStatus }))
    try {
      await updateTournamentService(buildTournamentPayload(newStatus))
      notify.success(newStatus === 'published' ? 'Tournament published' : 'Tournament set to draft')
      // Reflect any cascaded bracket statuses from a successful publish.
      await loadTournament()
    } catch (error) {
      console.error('Error updating tournament status:', error)
      notify.error(error.message || 'Failed to update status')
      setFormData(prev => ({ ...prev, status: previous }))
    }
  }

  const handleBracketCreated = (newBracket) => {
    navigate(`/bracket/${newBracket.id}/seed`)
  }

  // DUB-93/95/96/97: status changes here are sticky — no read derivation will
  // overwrite. Apply directly; the dropdown selection IS the confirmation.
  const handleBracketStatusChange = async (bracket, newStatus) => {
    if (bracket.status === newStatus) return
    const previous = bracket.status
    setBrackets(prev => prev.map(b => (b.id === bracket.id ? { ...b, status: newStatus } : b)))
    try {
      await updateBracketStatus(bracket.id, newStatus)
      notify.success(`Bracket ${newStatus === 'locked' ? 'locked' : 'open for picks'}`)
    } catch (err) {
      console.error('Error updating bracket status:', err)
      notify.error(err.message || 'Failed to update bracket status')
      setBrackets(prev => prev.map(b => (b.id === bracket.id ? { ...b, status: previous } : b)))
    }
  }

  const handleDeleteBracket = async (bracketId) => {
    const bracket = brackets.find(b => b.id === bracketId)
    const confirmed = await notify.confirm({
      title: 'Delete Bracket',
      description: `Are you sure you want to delete "${bracket?.name || 'this bracket'}"? This will permanently delete all entries, picks, seeds, and teams associated with this bracket. This action cannot be undone.`,
      variant: 'destructive',
      confirmText: 'Delete Bracket',
      cancelText: 'Cancel'
    })
    if (!confirmed) return

    try {
      const data = await deleteBracketService({ bracketId })
      if (data?.success) {
        notify.success(data.message || 'Bracket deleted successfully')
        setBrackets(prev => prev.filter(b => b.id !== bracketId))
      } else {
        notify.error(data?.error || 'Failed to delete bracket')
      }
    } catch (err) {
      console.error('Error deleting bracket:', err)
      notify.error('Failed to delete bracket. Please try again.')
    }
  }

  const handleDeleteTournament = async () => {
    const confirmed = await notify.confirm({
      title: 'Delete Tournament',
      description: `Are you sure you want to delete "${formData.title}"? This will permanently delete all brackets, picks, and entries. This action cannot be undone.`,
      variant: 'destructive',
      confirmText: 'Delete Tournament',
      cancelText: 'Cancel'
    })
    if (!confirmed) return

    try {
      const data = await deleteTournamentService({ tournamentId: id })
      if (data?.success) {
        notify.success(data.message || 'Tournament deleted successfully')
        navigate('/admin/dashboard?tab=tournaments')
      } else {
        notify.error(data?.error || 'Failed to delete tournament')
      }
    } catch (err) {
      console.error('Error deleting tournament:', err)
      notify.error('Failed to delete tournament. Please try again.')
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">Loading tournament...</div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <BackLink label="Tournaments" to="/admin/dashboard?tab=tournaments" className="mb-4" />
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="text-2xl sm:text-3xl font-bold">Edit Tournament</h1>
            {formData.isTest && (
              <Badge variant="muted" className="uppercase tracking-wide text-[10px]">Test mode</Badge>
            )}
          </div>
          <p className="text-muted-foreground">Update tournament details and manage brackets</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between space-y-0">
              <div>
                <CardTitle>Brackets</CardTitle>
                <CardDescription>Add, seed, and remove brackets for this tournament</CardDescription>
              </div>
              <Button size="sm" onClick={() => setShowAddBracketModal(true)} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Add Bracket
              </Button>
            </CardHeader>
            <CardContent>
              {brackets.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No brackets yet. Click &quot;Add Bracket&quot; to create one.
                </p>
              ) : (
                <div className="space-y-2">
                  {brackets.map(bracket => (
                    <div
                      key={bracket.id}
                      className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 border rounded-md bg-muted/30"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium truncate">{bracket.name}</span>
                            {EDITABLE_STATUSES.includes(bracket.status) ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Badge
                                    variant={statusBadgeVariant(bracket.status)}
                                    className="cursor-pointer hover:opacity-80 transition-opacity gap-1.5"
                                  >
                                    {statusLabel(bracket.status)}
                                    <ChevronDown className="h-3 w-3 opacity-60" />
                                  </Badge>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                  {STATUS_DROPDOWN_OPTIONS.map(option => (
                                    <DropdownMenuItem
                                      key={option.value}
                                      onClick={() => handleBracketStatusChange(bracket, option.value)}
                                      className="flex items-center justify-between gap-6"
                                    >
                                      <span className="flex items-center gap-2">
                                        <span className={`h-2 w-2 rounded-full ${option.dot}`} />
                                        {option.label}
                                      </span>
                                      {bracket.status === option.value && (
                                        <Check className="h-4 w-4 text-primary" />
                                      )}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : (
                              <Badge variant={statusBadgeVariant(bracket.status)}>
                                {statusLabel(bracket.status)}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {bracket.entryCount} {bracket.entryCount === 1 ? 'entry' : 'entries'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/bracket/${bracket.id}/seed`)}
                          className="flex-1 sm:flex-none"
                        >
                          <Edit className="h-4 w-4 sm:mr-1" />
                          <span className="ml-1.5 sm:ml-0">Edit</span>
                          <span className="hidden sm:inline ml-1">Bracket</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/bracket/${bracket.id}/rounds`)}
                          className="flex-1 sm:flex-none"
                        >
                          <CalendarClock className="h-4 w-4 sm:mr-1" />
                          <span className="ml-1.5 sm:ml-0">Rounds</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEnterOfficialPicks(bracket)}
                          className="flex-1 sm:flex-none"
                        >
                          <FileCheck className="h-4 w-4 sm:mr-1" />
                          <span className="ml-1.5 sm:ml-0 sm:hidden">Results</span>
                          <span className="hidden sm:inline">Enter Results</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteBracket(bracket.id)}
                          className="text-destructive hover:text-destructive shrink-0"
                          aria-label={`Delete ${bracket.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Tournament Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="e.g., PPA World Championships"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
                  <Select value={formData.status} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-md border border-dashed p-3 bg-muted/30">
                <input
                  id="tournament-is-test"
                  type="checkbox"
                  checked={formData.isTest}
                  onChange={(e) => handleInputChange('isTest', e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="tournament-is-test" className="text-sm font-medium cursor-pointer">
                  Test mode
                </label>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Start Date *</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">End Date *</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => handleInputChange('endDate', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Deadline Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={formData.submissionDeadline}
                      onChange={(e) => handleInputChange('submissionDeadline', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Deadline Time (ET)</label>
                  <Input
                    type="time"
                    value={formData.submissionDeadlineTime}
                    onChange={(e) => handleInputChange('submissionDeadlineTime', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/dashboard?tab=tournaments')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-destructive">Danger Zone</CardTitle>
              <CardDescription>Irreversible actions</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="destructive"
                onClick={handleDeleteTournament}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Tournament
              </Button>
            </CardContent>
          </Card>
        </div>

        <AddBracketModal
          isOpen={showAddBracketModal}
          onClose={() => setShowAddBracketModal(false)}
          tournamentId={id}
          sportId={sportId}
          existingDivisionIds={brackets.map(b => b.divisionId).filter(Boolean)}
          hasPoolPlayBracket={brackets.some(b => b.format === 'pool_then_sunday')}
          onBracketCreated={handleBracketCreated}
        />
      </div>
    </Layout>
  )
}
